import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublicData } from '../../context/PublicDataContext';
import { img, Eyebrow, Display } from './components/ui';
import Seo from './components/Seo';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

export default function HomePage() {
  const { settings, sections, products, process, testimonials, team, siteName, loading } = usePublicData();
  const [form, setForm] = useState({ name: '', phone: '', email: '', enquiry_type: 'Dealer / Retailer', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/website/public/enquiry', form);
      toast.success("Thanks! We'll get back to you shortly.");
      setForm({ name: '', phone: '', email: '', enquiry_type: 'Dealer / Retailer', message: '' });
    } catch {
      toast.error('Could not send enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const about = sections.about || {};

  return (
    <>
      <Seo
        title={settings.seo_default_title || undefined}
        description={settings.seo_default_description || undefined}
        image={settings.og_image ? img(settings.og_image, undefined) : undefined}
        siteName={siteName}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteName,
          url: typeof window !== 'undefined' ? window.location.origin : undefined,
          description: settings.seo_default_description || undefined,
        }}
      />

      {/* ---------- Hero ---------- */}
      <section className="relative min-h-screen flex items-end">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${img(settings.hero_image)})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,9,0.55) 0%, rgba(20,13,9,0.35) 40%, rgba(15,10,7,0.92) 100%)' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-24 pb-24 w-full text-white">
          <div className="flex items-center gap-4 text-[#d98a52] uppercase tracking-[0.28em] text-xs font-medium mb-6">
            <span className="w-9 h-px bg-[#d98a52]" /> Furniture Manufacturing &amp; Export
          </div>
          <h1 className="font-display font-semibold leading-[1.05] max-w-3xl mb-6" style={{ fontSize: 'clamp(34px,6vw,68px)' }}>
            {settings.hero_heading || 'Furniture built on craft, engineered for scale.'}
          </h1>
          <p className="text-[#e6dbc9] max-w-lg mb-10 font-light text-[17px]">
            {settings.hero_subheading || `${siteName} designs and manufactures furniture for dealers, hospitality projects, and retail brands across India.`}
          </p>
          <div className="flex gap-4 flex-wrap mb-14">
            <a href="#contact" className="px-7 py-3.5 text-[13px] uppercase tracking-wide bg-[#b5622f] hover:bg-[#d98a52] transition-colors">Request a Quote</a>
            <Link to="/products" className="px-7 py-3.5 text-[13px] uppercase tracking-wide border border-white/50 hover:border-white hover:bg-white/10 transition-all">View Product Range</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 border-t border-white/20 pt-7 max-w-3xl">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className={i > 1 ? 'md:border-l border-white/20 md:pl-6' : ''}>
                <div className="font-display font-semibold text-3xl">{settings[`stat_${i}_num`] || '—'}</div>
                <div className="text-[#cbbca4] text-[11px] uppercase tracking-wide mt-1">{settings[`stat_${i}_label`] || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Trusted By strip ---------- */}
      <div className="py-10 border-b border-black/10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#4a3d32] flex-shrink-0">Trusted By</span>
          <div className="flex flex-wrap justify-center md:justify-start gap-x-10 gap-y-3">
            {['Furniture Dealers', 'Hospitality Groups', 'Interior Studios', 'Retail Showrooms', 'Export Partners'].map(c => (
              <span key={c} className="text-[13px] uppercase tracking-wide text-[#221a14]/60 font-medium">{c}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- About ---------- */}
      <section id="about" className="py-28 md:py-32 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="relative h-[420px] hidden md:block">
            <img src={img(about.image_url)} alt="About" className="absolute w-[72%] h-[80%] top-0 left-0 object-cover shadow-2xl" />
            <img src={img(about.image_url_2)} alt="Craftsmanship" className="absolute w-[52%] h-[56%] bottom-0 right-0 object-cover border-4 border-[#f4ece0] shadow-2xl" />
          </div>
          <img src={img(about.image_url)} alt="About" className="md:hidden w-full h-72 object-cover mb-4" />
          <div>
            <Eyebrow>{about.subtitle || 'About the Company'}</Eyebrow>
            <Display as="h2" className="text-3xl md:text-4xl mt-4 mb-6 leading-tight">
              {about.title || 'Two generations of woodwork, one modern factory floor.'}
            </Display>
            <p className="text-[#4a3d32] text-[15.5px] leading-relaxed whitespace-pre-line">
              {about.body || 'What began as a small workshop has grown into a full-scale manufacturing unit producing furniture for homes, offices, and hospitality spaces.'}
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Team ---------- */}
      {team.length > 0 && (
        <section id="team" className="py-28 md:py-32 bg-[#ece0cd] scroll-mt-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-xl mb-14">
              <Eyebrow>Our Team</Eyebrow>
              <Display as="h2" className="text-3xl md:text-4xl mt-4">The people behind the work</Display>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {team.map(person => (
                <div key={person.id} className="text-center">
                  <div className="aspect-square overflow-hidden rounded-full w-32 h-32 md:w-40 md:h-40 mx-auto mb-5 shadow-lg">
                    <img src={img(person.image_url)} alt={person.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-display font-semibold text-lg">{person.name}</h3>
                  <p className="text-[#b5622f] text-xs uppercase tracking-wide mt-1">{person.designation}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- Products ---------- */}
      <section id="products" className="bg-[#241811] text-white py-28 md:py-32 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-xl mb-14 flex items-end justify-between flex-wrap gap-4">
            <div>
              <Eyebrow tone="light">Product Range</Eyebrow>
              <Display as="h2" className="text-3xl md:text-4xl mt-4">What we manufacture</Display>
            </div>
            <Link to="/products" className="text-[12px] uppercase tracking-[0.14em] text-[#d98a52] border-b border-[#d98a52] pb-0.5 hover:opacity-80">
              View entire collection
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0.5 bg-white/10">
            {(products.length ? products.slice(0, 8) : []).map(p => (
              <Link to={`/products/${p.slug}`} key={p.id} className="relative aspect-[3/4] overflow-hidden bg-[#1a120c] group block">
                <img src={img(p.image_url)} alt={p.name} className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(10,7,4,0.92) 100%)' }} />
                <div className="absolute left-0 right-0 bottom-0 p-5">
                  <h3 className="font-display font-semibold text-lg">{p.name}</h3>
                  <span className="text-[11px] uppercase tracking-wide text-[#d98a52] opacity-0 group-hover:opacity-100 transition-opacity">View Details →</span>
                </div>
              </Link>
            ))}
            {!products.length && !loading && (
              <p className="col-span-4 text-[#cbbca4] text-sm py-10 text-center">Product categories will appear here once added from the admin panel.</p>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Process ---------- */}
      <section id="process" className="py-28 md:py-32 bg-[#ece0cd] scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-xl mb-10">
            <Eyebrow>How It's Made</Eyebrow>
            <Display as="h2" className="text-3xl md:text-4xl mt-4">From sketch to dispatch</Display>
          </div>
          <div>
            {process.map((step, i) => (
              <div key={step.id} className={`grid md:grid-cols-[110px_1fr_1.1fr] gap-6 md:gap-10 items-center py-9 border-t border-black/10 ${i === process.length - 1 ? 'border-b' : ''}`}>
                <div className="font-display text-4xl font-light text-[#b5622f]">{String(i + 1).padStart(2, '0')}</div>
                <div>
                  <h3 className="font-display font-semibold text-xl mb-2">{step.title}</h3>
                  <p className="text-[#4a3d32] text-sm max-w-md">{step.description}</p>
                </div>
                <div className="h-32 overflow-hidden order-first md:order-none">
                  <img src={img(step.image_url)} alt={step.title} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
          {process.length > 0 && (
            <div className="mt-10">
              <Link to="/craftsmanship" className="text-[12px] uppercase tracking-[0.14em] text-[#b5622f] border-b border-[#b5622f] pb-0.5 hover:opacity-80">
                See the full craftsmanship story
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ---------- Testimonials ---------- */}
      {testimonials.length > 0 && (
        <section className="py-28 bg-[#f4ece0]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-xl mb-10">
              <Eyebrow>Client Feedback</Eyebrow>
              <Display as="h2" className="text-3xl md:text-4xl mt-4">What our dealers say</Display>
            </div>
            <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
              {testimonials.map(t => (
                <div key={t.id} className="bg-[#f4ece0] p-9">
                  <div className="text-[#b5622f] text-sm mb-5">{'★'.repeat(t.rating || 5)}</div>
                  <p className="italic text-[15px] mb-6 leading-relaxed">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#b5622f] text-white flex items-center justify-center font-display text-sm">
                      {t.client_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{t.client_name}</div>
                      <div className="text-xs text-[#4a3d32]">{t.client_role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CTA band ---------- */}
      <div className="text-center py-16 text-white" style={{ backgroundImage: 'linear-gradient(120deg, #b5622f, #8a5a34)' }}>
        <h2 className="font-display font-semibold text-2xl md:text-4xl mb-6 px-6">Have a bulk order or project in mind?</h2>
        <a href="#contact" className="px-7 py-3.5 text-[13px] uppercase tracking-wide border border-white/70 hover:border-white hover:bg-white/10 transition-all">Talk to Our Team</a>
      </div>

      {/* ---------- Contact ---------- */}
      <section id="contact" className="py-28 md:py-32 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[0.9fr_1.1fr] gap-16">
          <div>
            <Eyebrow>Get In Touch</Eyebrow>
            <h3 className="font-display font-semibold text-2xl mt-4 mb-5">Request a catalog or quote</h3>
            <p className="text-[#4a3d32] text-[14.5px] mb-8">Fill in the form and our team will get back with pricing, lead times and sample options for your order.</p>

            <div className="flex gap-4 py-4 border-t border-black/10">
              <MapPin className="w-4 h-4 text-[#b5622f] mt-1 flex-shrink-0" />
              <div className="text-[14.5px]">{settings.address || 'Address not set yet'}</div>
            </div>
            <div className="flex gap-4 py-4 border-t border-black/10">
              <Phone className="w-4 h-4 text-[#b5622f] mt-1 flex-shrink-0" />
              <div className="text-[14.5px]">{settings.phone || 'Phone not set yet'}</div>
            </div>
            <div className="flex gap-4 py-4 border-t border-black/10">
              <Mail className="w-4 h-4 text-[#b5622f] mt-1 flex-shrink-0" />
              <div className="text-[14.5px]">{settings.email || 'info@mojiinnovators.com'}</div>
            </div>
            <div className="flex gap-4 py-4 border-t border-b border-black/10">
              <Clock className="w-4 h-4 text-[#b5622f] mt-1 flex-shrink-0" />
              <div className="text-[14.5px]">{settings.business_hours || 'Mon – Sat, 9:30 AM – 6:30 PM'}</div>
            </div>
          </div>

          <form onSubmit={submitEnquiry} className="grid gap-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#4a3d32] block mb-2">Full Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3.5 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" placeholder="Your name" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#4a3d32] block mb-2">Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3.5 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" placeholder="+91 00000 00000" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#4a3d32] block mb-2">Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3.5 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" placeholder="you@company.com" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-[#4a3d32] block mb-2">I'm Enquiring As</label>
                <select value={form.enquiry_type} onChange={e => setForm({ ...form, enquiry_type: e.target.value })} className="w-full px-4 py-3.5 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]">
                  <option>Dealer / Retailer</option>
                  <option>Interior Designer</option>
                  <option>Hospitality / Project Buyer</option>
                  <option>Individual Customer</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[#4a3d32] block mb-2">Requirement Details</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4} className="w-full px-4 py-3.5 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" placeholder="Tell us what you're looking for..." />
            </div>
            <div className="flex items-center gap-5">
              <button disabled={submitting} type="submit" className="px-7 py-3.5 text-[13px] uppercase tracking-wide bg-[#241811] text-white hover:bg-[#3d2b1f] transition-colors disabled:opacity-60">
                {submitting ? 'Sending…' : 'Send Enquiry'}
              </button>
              <span className="text-xs text-[#4a3d32]">We usually respond within 24 hours.</span>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
